import React, { useCallback, useRef } from 'react';
import * as Styled from './VHOverlay.styled';
import VHProfileInfo from '../molecules/VHProfileInfo/VHProfileInfo';
import FullscreenVHButton from '../FullscreenVHButton/FullscreenVHButton';
import WebPlayerTimer from '../molecules/WebPlayerTimer/WebPlayerTimer';
import { useShouldShowHints } from '../vh-types/vhTypeHooks';
import { useSelector, useDispatch } from 'react-redux';
import {
  defaultOrganisationMicBehaviorSelector,
  onscreenObjectivesAndHintsEnabledSelector,
} from '../../slices/VirtualHumanSlice';
import ObjectiveGroupsList from '../ObjectiveGroupsList/ObjectiveGroupsList';
import ObjectiveHint from '../ObjectiveHint/ObjectiveHint';
import {
  setSidebarVisible,
  sidebarVisibleSelector,
  toggleOverlay,
} from '../../slices/vh-player/VHPlayerSlice';
import VHSidebar from './VHSidebar/VHSidebar';
import OverlayToggleButton from './OverlayToggleButton/OverlayToggleButton';
import MicMutedProvider from '../VHChatInputArea/MicMutedProvider.context';
import VhTypeInputSelector from '../vh-types/vh-type-component-selectors/VHInputSelector/VHInputSelector';
import ActiveChat from './ActiveChat/ActiveChat';
import { objectiveGroupsWithObjectivesAndPointsSelector } from '../../slices/PatientSlice';
import CompleteSessionButton from '@vh/components/molecules/CompleteSessionButton/CompleteSessionButton';
import NarrowScreenWarningPopover from './NarrowScreenWarningPopover/NarrowScreenWarningPopover';
import MicBehaviorProvider from '@vh/components/VHChatInputArea/MicBehaviorProvider.context';
import { IsMobile } from '@base/utility/BrowserCheck';

const VHOverlay = ({ wrapperRef }) => {
  const onscreenObjectivesAndHintsEnabled = useSelector(
    onscreenObjectivesAndHintsEnabledSelector
  );
  const fullscreenButtonWrapperRef = useRef(null);
  const overlayRef = useRef(null);
  const shouldShowHints = useShouldShowHints();
  const dispatch = useDispatch();
  const handleToggleObjectives = useCallback(() => {
    dispatch(toggleOverlay('objectives'));
  }, [dispatch]);
  const objectiveOverlayVisible = useSelector(
    (state) => state.vhPlayer.overlays.objectives.visible
  );
  const sidebarVisible = useSelector(sidebarVisibleSelector);
  const handleToggleSidebar = useCallback(() => {
    dispatch(setSidebarVisible(!sidebarVisible));
  }, [dispatch, sidebarVisible]);

  const objectiveGroupsWithObjectivesAndPoints = useSelector(
    objectiveGroupsWithObjectivesAndPointsSelector
  );
  const showObjectives =
    onscreenObjectivesAndHintsEnabled &&
    objectiveGroupsWithObjectivesAndPoints.length > 0;
  const defaultOrganisationBehavior = useSelector(
    defaultOrganisationMicBehaviorSelector
  );
  const isMobile = IsMobile();

  return (
    <Styled.Overlay ref={overlayRef}>
      <Styled.RowOne>
        <Styled.SubWrapper>
          <VHProfileInfo />
        </Styled.SubWrapper>
        <Styled.CornerWrapper>
          <Styled.SubWrapper>
            <WebPlayerTimer />
          </Styled.SubWrapper>
          <Styled.OverlayToggleWrapper>
            <CompleteSessionButton />
          </Styled.OverlayToggleWrapper>
          {showObjectives && (
            <Styled.OverlayToggleWrapper>
              <OverlayToggleButton
                onClick={handleToggleObjectives}
                expanded={objectiveOverlayVisible}
                location="bottom"
                overlayNameKey="objectives"
                icon="objective"
              />
            </Styled.OverlayToggleWrapper>
          )}
          <Styled.OverlayToggleWrapper>
            <OverlayToggleButton
              onClick={handleToggleSidebar}
              expanded={sidebarVisible}
              location="bottom"
              overlayNameKey="sidebar"
              icon="menu"
            />
          </Styled.OverlayToggleWrapper>
          <Styled.FullScreenButtonWrapper ref={fullscreenButtonWrapperRef}>
            <NarrowScreenWarningPopover
              attachToRef={fullscreenButtonWrapperRef}
              checkDimensionsRef={overlayRef}
            />
            <FullscreenVHButton fullscreenElementRef={wrapperRef} />
          </Styled.FullScreenButtonWrapper>
        </Styled.CornerWrapper>
      </Styled.RowOne>
      <Styled.ObjectivesArea>
        {showObjectives && (
          <Styled.ObjectiveGroupsWrapper $visible={objectiveOverlayVisible}>
            <ObjectiveGroupsList />
          </Styled.ObjectiveGroupsWrapper>
        )}
      </Styled.ObjectivesArea>
      <Styled.SidebarWrapper>
        <Styled.SidebarSubwrapper $visible={sidebarVisible}>
          <VHSidebar />
        </Styled.SidebarSubwrapper>
      </Styled.SidebarWrapper>
      <Styled.HintWrapper $visible={objectiveOverlayVisible}>
        {onscreenObjectivesAndHintsEnabled && shouldShowHints && (
          <ObjectiveHint />
        )}
      </Styled.HintWrapper>
      <Styled.InputWrapper $hasHints={shouldShowHints} $isMobile={isMobile}>
        <ActiveChat />
        <MicBehaviorProvider defaultMicBehavior={defaultOrganisationBehavior}>
          <MicMutedProvider>
            {/* <AudioMediaRecorderProvider>
              <DeepgramWebSocketConnectionProvider language={vhDBVH.language}> */}
            <VhTypeInputSelector />
            {/* </DeepgramWebSocketConnectionProvider>
            </AudioMediaRecorderProvider> */}
          </MicMutedProvider>
        </MicBehaviorProvider>
      </Styled.InputWrapper>
      <Styled.Empty />
    </Styled.Overlay>
  );
};

export default VHOverlay;
